import { gql, useQuery } from "@apollo/client";
import { Noun } from "@asmbl/shared/permissions";
import { contramap } from "@asmbl/shared/sort";
import { CircularProgress } from "@mui/material";
import { upperFirst } from "lodash";
import { memo } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "src/components/Auth/AuthContext";
import {
  CompCycleFilterBarQuery,
  CompCycleFilterBarQueryVariables,
  GetParticipantsInputGuidanceType,
  GetParticipantsInputReports,
} from "../../../__generated__/graphql";
import {
  SearchableFilter,
  SearchableFilterOption,
} from "../../../components/Filter/SearchableFilter";
import { SearchableFilterContainer } from "../../../components/Filter/SearchableFilterContainer";
import { BandPlacementDisplay } from "../../../models/Band";
import { CompItemRecommendationTypeTitle } from "../../../models/CompRecommendation";
import { usePagination } from "./Contexts/PaginationContext";
import { useTableFilterContext } from "./Contexts/TableFilterContext";

type Props = {
  compCycleId: number;
};

enum FilterName {
  MANAGER = "Manager",
  REPORTS = "Reports",
  STATUS = "Status",
  CURRENT_DEPARTMENT = "Current Department",
  CURRENT_LADDER = "Current Ladder",
  CURRENT_LEVEL = "Current Level",
  LOCATION_GROUP = "Location Group",
  PERFORMANCE_RATING = "Performance Rating",
  ADJUSTMENT_TYPE = "Adjustment Type",
  CURRENT_BAND_POSITION = "Current Band Position",
  MERIT_GUIDANCE_FILTER = "Merit Guidance",
}

const guidanceFilterOptions = [
  GetParticipantsInputGuidanceType.above,
  GetParticipantsInputGuidanceType.on_target,
  GetParticipantsInputGuidanceType.below,
  GetParticipantsInputGuidanceType.none,
].map((value) => ({
  label: upperFirst(value.split("_").join(" ")),
  value,
}));
function CompCycleFilterBar({ compCycleId }: Props): JSX.Element | null {
  const { employeeId, permissions } = useAuth();
  const [urlParams] = useSearchParams();
  const { setCurrentPage } = usePagination();
  const { filter, handleFilterChange, handleFilterReset } =
    useTableFilterContext();
  const { data } = useQuery<
    CompCycleFilterBarQuery,
    CompCycleFilterBarQueryVariables
  >(CompCycleFilterBar.query, {
    variables: {
      compCycleId,
    },
  });

  if (!data) return <CircularProgress />;

  const managerOptions = (
    permissions.isHRBP() ? data.actableManagers ?? [] : data.managers
  )
    .map((m) => option(m.displayName ?? "", m.id))
    .sort(contramap((o) => o.label));

  const reportOptions = [
    option("Direct", GetParticipantsInputReports.direct),
    option("Indirect", GetParticipantsInputReports.indirect),
  ];

  // Location Groups, sorted first by Market name, and then Location Group name.
  const locationOptions = data.markets
    .flatMap((m) => m.locationGroups.map((l) => option(l.name, l.id, m.name)))
    .sort(contramap((o) => o.label))
    .sort(contramap((o) => o.group));

  const departmentOptions = data.organization.departments
    .map((d) => option(d.name, d.id))
    .sort(contramap((o) => o.label));

  const ladderOptions = data.organization.departments
    .flatMap((d) => d.ladders.map((l) => option(l.name, l.id)))
    .sort(contramap((o) => o.label));

  const levelOptions =
    data.organization.compStructure?.levels
      .map((l) => option(l, l))
      .sort(contramap((o) => o.value)) ?? [];

  const perfRatingOptions =
    data.compCycle?.perfRatings
      .map((p) => option(p, p))
      .sort(contramap((o) => o.label)) ?? [];

  const adjustmentTypeOptions = Object.entries(
    CompItemRecommendationTypeTitle
  ).map(([key, value]) => option(value, key));

  const bandPlacementOptions = Object.entries(BandPlacementDisplay).map(
    ([key, value]) => option(value, key)
  );

  const activeFilterMap: Record<
    Exclude<FilterName, FilterName.STATUS>,
    boolean
  > = {
    [FilterName.MANAGER]: (filter.managerIds?.length ?? 0) > 0,
    [FilterName.LOCATION_GROUP]: (filter.locationGroupIds?.length ?? 0) > 0,
    [FilterName.CURRENT_DEPARTMENT]: (filter.departmentIds?.length ?? 0) > 0,
    [FilterName.CURRENT_LADDER]: (filter.ladderIds?.length ?? 0) > 0,
    [FilterName.CURRENT_LEVEL]: (filter.levels?.length ?? 0) > 0,
    [FilterName.PERFORMANCE_RATING]: (filter.perfRatings?.length ?? 0) > 0,
    [FilterName.ADJUSTMENT_TYPE]: (filter.recItems?.length ?? 0) > 0,
    [FilterName.REPORTS]: (filter.reports?.length ?? 0) > 0,
    [FilterName.CURRENT_BAND_POSITION]: (filter.bandPlacement?.length ?? 0) > 0,
    [FilterName.MERIT_GUIDANCE_FILTER]:
      (filter.guidanceFilters?.length ?? 0) > 0,
  } as const;

  const activeFilters = Object.entries(activeFilterMap)
    .filter(([, value]) => value)
    .map(([key]) => key);

  const handleFiltersReset = () => {
    handleFilterReset();
    setCurrentPage(0);
  };

  const handleFilterNumbersChange = (
    filterType:
      | "managerIds"
      | "departmentIds"
      | "ladderIds"
      | "levels"
      | "locationGroupIds",
    values: number[]
  ) => {
    handleFilterChange(filterType, values);
    setCurrentPage(0);
  };

  const handleFilterStringChange = (
    filterType: "perfRatings" | "recItems" | "bandPlacement",
    values: string[]
  ) => {
    handleFilterChange(filterType, values);
    setCurrentPage(0);
  };

  const handleGuidanceFilterChange = (
    values: GetParticipantsInputGuidanceType[]
  ) => {
    handleFilterChange("guidanceFilters", values);
    setCurrentPage(0);
  };

  const handleReportsFilterChange = (values: GetParticipantsInputReports[]) => {
    handleFilterChange("reports", values);
    setCurrentPage(0);
  };

  return (
    <SearchableFilterContainer
      activeFilters={activeFilters}
      onReset={handleFiltersReset}
    >
      <SearchableFilter
        name={FilterName.MANAGER}
        options={managerOptions}
        selected={filter.managerIds ?? []}
        onChange={(values) => handleFilterNumbersChange("managerIds", values)}
        multiple={!permissions.isHRBP()}
        disableChip={
          !permissions.canViewGlobal(Noun.Employee) &&
          urlParams.has("manager") &&
          filter.managerIds?.length === 1 &&
          filter.managerIds[0] === employeeId
        }
      />
      <SearchableFilter
        name={FilterName.REPORTS}
        options={reportOptions}
        selected={getSelectedReportsFilter(filter.reports)}
        onChange={(values) => handleReportsFilterChange(values)}
        disabled={(filter.managerIds?.length ?? 0) === 0}
      />
      <SearchableFilter
        name={FilterName.CURRENT_DEPARTMENT}
        options={departmentOptions}
        selected={filter.departmentIds ?? []}
        onChange={(values) =>
          handleFilterNumbersChange("departmentIds", values)
        }
      />
      <SearchableFilter
        name={FilterName.CURRENT_LADDER}
        options={ladderOptions}
        selected={filter.ladderIds ?? []}
        onChange={(values) => handleFilterNumbersChange("ladderIds", values)}
      />
      <SearchableFilter
        name={FilterName.CURRENT_LEVEL}
        options={levelOptions}
        selected={filter.levels ?? []}
        onChange={(values) => handleFilterNumbersChange("levels", values)}
      />
      <SearchableFilter
        name={FilterName.LOCATION_GROUP}
        options={locationOptions}
        selected={filter.locationGroupIds ?? []}
        onChange={(values) =>
          handleFilterNumbersChange("locationGroupIds", values)
        }
      />
      <SearchableFilter
        name={FilterName.PERFORMANCE_RATING}
        options={perfRatingOptions}
        selected={filter.perfRatings ?? []}
        onChange={(values) => handleFilterStringChange("perfRatings", values)}
      />
      <SearchableFilter
        name={FilterName.ADJUSTMENT_TYPE}
        options={adjustmentTypeOptions}
        selected={filter.recItems ?? []}
        onChange={(values) => handleFilterStringChange("recItems", values)}
      />
      <SearchableFilter
        name={FilterName.CURRENT_BAND_POSITION}
        options={bandPlacementOptions}
        selected={filter.bandPlacement ?? []}
        onChange={(values) => handleFilterStringChange("bandPlacement", values)}
      />
      <SearchableFilter
        name={FilterName.MERIT_GUIDANCE_FILTER}
        options={guidanceFilterOptions}
        selected={filter.guidanceFilters ?? []}
        onChange={(values) => handleGuidanceFilterChange(values)}
      />
    </SearchableFilterContainer>
  );
}

export default memo(CompCycleFilterBar);

CompCycleFilterBar.query = gql`
  query CompCycleFilterBarQuery($compCycleId: Int!) {
    organization {
      id
      compStructure {
        id
        levels
      }
      departments {
        id
        name
        ladders {
          id
          name
        }
      }
    }
    compCycle(id: $compCycleId) {
      id
      perfRatings
    }
    managers {
      id
      displayName
    }
    actableManagers(compCycleId: $compCycleId) {
      id
      displayName
    }
    markets {
      id
      name
      locationGroups {
        id
        name
      }
    }
  }
`;

function option<T>(
  label: string | number,
  value: T
): { label: string; value: T; group: undefined };
function option<T>(
  label: string | number,
  value: T,
  group: string
): { label: string; value: T; group: string };
function option<T>(
  label: string | number,
  value: T,
  group?: string
): SearchableFilterOption<T> {
  return { label: label.toString(), value, group };
}

// Since "All" is a valid option on the server but not the client, we have to
// turn it into a combination of direct + indirect reports in the UI.
function getSelectedReportsFilter(
  reports: GetParticipantsInputReports | null | undefined
): GetParticipantsInputReports[] {
  if (reports == null) return [];
  if (reports === GetParticipantsInputReports.all) {
    return [
      GetParticipantsInputReports.direct,
      GetParticipantsInputReports.indirect,
    ];
  }
  return [reports];
}
